import React from 'react';
import { Cards, WarningCircle } from '@phosphor-icons/react';

import { MarketShell } from '../../design-system/components/shells';
import { Empty } from '../../design-system/components/desktop';
import { useViewport } from '../../design-system/hooks/useViewport';
import { routes } from '../../routes/routes';
import { errorText, LOAD_FAILURE } from '../../utils/errorText';
import { Tile, TileGrid, TileGridSkeleton, TileIndexHeader, monogram } from './tiles';

/**
 * Sets, the view. Archetype A10, tile index, no filters.
 *
 * Pure: every input arrives as a prop, every output leaves as a callback. The
 * container (`SetsPage.jsx`) owns the fetch and the transform, which is what
 * lets the state explorer mount this exact code with fixture data.
 *
 * WHAT THIS ENDPOINT ACTUALLY RETURNS, AND THEREFORE ALL THIS MAY SHOW
 * --------------------------------------------------------------------
 * `getTCGCategories()` gives an `id`, a `name`, an optional `image_url` and an
 * optional `description`. There is no set count, no card count and no release
 * date on it, so none appear here. Half the tiles legitimately have no image
 * and no description, and both branches have to look deliberate.
 *
 * NO FILTERS. Nothing on this page is filterable, which is why an empty index
 * means the CATALOGUE is empty rather than that a filter excluded everything.
 * That is why the empty state is the default tone and not `filtered`, and why
 * the one useful command on it is back to the marketplace.
 *
 * The app's "Back to Marketplace" button is the shell's job now: breadcrumbs on
 * desktop, the floating back button on mobile, never both.
 */

export function SetsView({ categories = [], loading = false, error = null, mediaUrl = (u) => u }) {
  const { isDesktop } = useViewport();
  let body;

  if (loading) {
    body = <TileGridSkeleton count={8} />;
  } else if (error) {
    body = (
      <Empty
        tone="error"
        icon={WarningCircle}
        illustration="request-failed"
        title="We could not load the games"
        text={errorText(error, LOAD_FAILURE)}
      />
    );
  } else if (categories.length === 0) {
    body = (
      <Empty
        icon={Cards}
        illustration="nothing-catalogued-yet"
        title="No trading card game categories found"
        text="Nothing has been catalogued yet. Everything currently for sale is still browsable from the marketplace."
      />
    );
  } else {
    body = (
      <TileGrid>
        {categories.map((tcg) => (
          <Tile
            key={tcg.id}
            to={`/sets/category/${tcg.id}`}
            image={tcg.image_url ? mediaUrl(tcg.image_url) : null}
            imageAlt={tcg.name}
            /* A monogram, not the first WORD — that printed the name twice
               ("Pokemon" above "Pokemon"). No game has a logo, so this plate
               is what every buyer sees. See `monogram` in tiles.jsx. */
            placeholder={monogram(tcg.name)}
            name={tcg.name}
            description={tcg.description || null}
          />
        ))}
      </TileGrid>
    );
  }

  return (
    <MarketShell
      activeNav="sets"
      /* Breadcrumbs are the DESKTOP back affordance and the mobile top bar
         derives its title from them, which would put "Sets" in the bar and
         again in the `<h1>` two lines below it. Below the handoff the floating
         back button is the affordance and the wordmark holds the bar, so the
         trail is not passed at all. */
      breadcrumbs={
        isDesktop ? [{ label: 'Marketplace', to: routes.MARKETPLACE }, { label: 'Games' }] : []
      }
      /* No `back`. /sets is a bottom-bar tab, so it is in the shell's
         SECTION_ROOTS — and the shell's own rule is that a back control on a
         section root is noise, because "back" from a root means leaving the
         app (MarketShell.jsx:102). Passing it explicitly overrode that guard
         unconditionally (MarketShell.jsx:206) and put a back button pointing at
         /marketplace on a screen whose tab bar already has /marketplace one
         position to the left: two controls, 700px apart, doing the same thing.
         TCGSetsPage's `back` to /sets is different and stays — a set category
         is genuinely below a root. */
    >
      {/* GAMES, not "Sets". This page lists trading card GAMES — it renders
          `getTCGCategories()`, one tile per game — and you pick a game before
          you ever see a set. The sets are one level down at
          `/sets/category/:tcgId`. The route keeps its `/sets` path because
          paths are not ours to change, but every label a buyer reads now says
          what the page actually shows. */}
      <TileIndexHeader title="Games" sub="Pick a trading card game to browse its sets." />
      {/* Skeleton bars are decorative and already `aria-hidden`, so the busy
          state is announced on the region that is loading, not bar by bar. */}
      <div aria-busy={loading || undefined}>{body}</div>
    </MarketShell>
  );
}

export default SetsView;
